import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Clock, CalendarDays, Wallet, FileText } from "lucide-react";
import { formatDateFr } from "../lib/dateFormat";

function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CollaborateurStats({ collaborateurId }) {
  const today = new Date();
  const defaultStart = useMemo(() => `${today.getFullYear()}-01-01`, []);
  const defaultEnd = useMemo(() => `${today.getFullYear()}-12-31`, []);
  const [range, setRange] = useState({ start_date: defaultStart, end_date: defaultEnd });
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/collaborateurs/${collaborateurId}/stats`, { params: range });
      setStats(data);
    } catch {
      setStats(null);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collaborateurId, range]);

  if (!stats) {
    return <div className="text-sm text-zinc-500 font-mono">Chargement…</div>;
  }

  const solde = stats.solde_conges;
  const leaves = stats.leaves || {};
  const breakdown = Object.entries(leaves.by_category || {});

  return (
    <div className="space-y-6" data-testid="collab-stats">
      {/* Date range */}
      <div className="flex items-end gap-3 flex-wrap">
        <div>
          <Label className="label-uppercase mb-1.5 block">Période — du</Label>
          <Input type="date" value={range.start_date} onChange={(e) => setRange({ ...range, start_date: e.target.value })} data-testid="stats-start-date" />
        </div>
        <div>
          <Label className="label-uppercase mb-1.5 block">au</Label>
          <Input type="date" value={range.end_date} onChange={(e) => setRange({ ...range, end_date: e.target.value })} data-testid="stats-end-date" />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="kpi-shifts-count">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays size={14} className="text-blue-600" />
            <p className="label-uppercase">Shifts planifiés</p>
          </div>
          <p className="text-2xl font-display font-bold tracking-tight">{stats.shifts_count}</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="kpi-hours-worked">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={14} className="text-emerald-600" />
            <p className="label-uppercase">Heures planifiées</p>
          </div>
          <p className="text-2xl font-display font-bold tracking-tight">{stats.hours_worked} h</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="kpi-leave-days">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={14} className="text-amber-600" />
            <p className="label-uppercase">Indisponibilités approuvées</p>
          </div>
          <p className="text-2xl font-display font-bold tracking-tight">{leaves.approved_days || 0}</p>
          {leaves.pending_days > 0 && (
            <p className="text-[11px] text-zinc-500 font-mono mt-1">+ {leaves.pending_days} en attente</p>
          )}
        </div>
        {solde ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4" data-testid="kpi-solde">
            <div className="flex items-center gap-2 mb-2">
              <Wallet size={14} className="text-emerald-700" />
              <p className="label-uppercase text-emerald-800">Solde de congés</p>
            </div>
            <p className="text-2xl font-display font-bold tracking-tight text-emerald-900" data-testid="solde-remaining">
              {solde.remaining} <span className="text-sm font-mono text-emerald-700">/ {solde.allowance} j</span>
            </p>
            <p className="text-[11px] text-emerald-700 font-mono mt-1">
              {solde.taken} pris · {solde.pending} en attente
            </p>
          </div>
        ) : (
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4" data-testid="kpi-no-solde">
            <p className="label-uppercase">Solde de congés</p>
            <p className="text-xs text-zinc-500 font-mono mt-2">
              Non applicable — catégorie non salariée
            </p>
          </div>
        )}
      </div>

      {/* Breakdown by category */}
      {breakdown.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-4">
          <p className="label-uppercase mb-3">Répartition par type d'indisponibilité</p>
          <div className="space-y-2">
            {breakdown.map(([name, info]) => (
              <div key={name} className="flex items-center gap-3 text-sm" data-testid={`leave-cat-${name}`}>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: info.color }} />
                <span className="flex-1">{name}</span>
                <span className="font-mono text-zinc-600 text-xs">{info.count} demande(s)</span>
                <span className="font-mono font-semibold text-zinc-900 w-16 text-right">{info.days} j</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent leaves */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200">
          <p className="label-uppercase">Historique des indisponibilités</p>
        </div>
        <div className="divide-y divide-zinc-100 max-h-80 overflow-y-auto">
          {(leaves.recent || []).length === 0 && (
            <p className="p-4 text-sm text-zinc-500 font-mono">Aucune indisponibilité enregistrée</p>
          )}
          {(leaves.recent || []).map((lv) => (
            <div key={lv.id} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`leave-row-${lv.id}`}>
              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-sm tracking-wider ${
                lv.status === "approuve" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                lv.status === "refuse" ? "bg-red-50 text-red-700 border border-red-200" :
                "bg-amber-50 text-amber-700 border border-amber-200"
              }`}>
                {lv.status === "approuve" ? "Approuvé" : lv.status === "refuse" ? "Refusé" : "En attente"}
              </span>
              <span className="font-mono text-xs text-zinc-600 flex-1">
                {formatDateFr(lv.start_date)} → {formatDateFr(lv.end_date)}
              </span>
              {lv.reason && <span className="text-xs text-zinc-500 italic truncate max-w-[40%]">{lv.reason}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
